import { createFileRoute, Link } from "@tanstack/react-router";
import { Section } from "@/components/Section";
import { GuideCardGrid, GuideFooter, GuideSteps } from "@/components/guide/GuideSections";
import { Reveal } from "@/components/Reveal";
import { JsonLd } from "@/components/JsonLd";
import { KeyTakeaways } from "@/components/KeyTakeaways";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { ReviewedBy } from "@/components/ReviewedBy";
import { SITE } from "@/content/site";
import { buildSeo, breadcrumbSchema } from "@/lib/seo";
import { faqSchema } from "@/lib/schema";

const CRUMBS = [
  { name: "Home", path: "/" },
  { name: "Resources", path: "/resources" },
  { name: "How To Improve Domain Authority", path: "/how-to-improve-domain-authority" },
];

const CHECKERS = [
  { t: "Moz — Domain Authority (DA)", d: "The original 0–100 score. Free checks through Link Explorer and the MozBar extension.", n: "Updates roughly monthly, so don't read week-to-week wobble as progress." },
  { t: "Semrush — Authority Score (AS)", d: "Blends backlink quality, organic traffic and spam signals, so it tracks real performance more closely.", n: "The score I quote in client reports, because it punishes bought links." },
  { t: "Ahrefs — Domain Rating (DR)", d: "Purely link-based: how strong your backlink profile is relative to everyone else's.", n: "Easiest to game, and therefore easiest to misread." },
];

const STEPS = [
  {
    t: "Earn links from sites that already rank for your topic",
    d: "One link from a site Google trusts in your niche moves authority further than fifty directory listings. Aim for industry publications, partner sites, suppliers, local chambers, and journalists covering your sector.",
    how: "Publish something worth citing — original data, a calculator, a benchmark — then pitch it to the ten people who write about that topic every month.",
  },
  {
    t: "Cut the links that are dragging you down",
    d: "Paid link networks, comment spam and expired-domain link farms are the main reason a score stalls or falls. Semrush's Authority Score drops when a profile looks purchased.",
    how: "Audit referring domains quarterly. Ask for removal first; disavow only the clearly toxic ones you can't get taken down.",
  },
  {
    t: "Build topical depth, not just page count",
    d: "Authority is topical as much as site-wide. Ten thorough pages that cover one subject end to end outrank forty thin pages spread across five subjects.",
    how: "Pick one hub topic, map the questions buyers actually ask, and publish until the cluster is complete.",
  },
  {
    t: "Fix internal linking so authority flows",
    d: "Link equity that lands on your homepage and stops there is wasted. Orphan pages inherit nothing.",
    how: "Link from your strongest pages down to the ones you want to rank, using descriptive anchor text — not \"click here\".",
  },
  {
    t: "Get the technical basics out of the way",
    d: "Crawlability, indexation, HTTPS, Core Web Vitals and clean canonicals don't raise your score by themselves. They stop everything else from being wasted.",
    how: "Run a crawl monthly. Broken redirects and noindexed pages quietly bleed authority you already paid for.",
  },
  {
    t: "Make the brand verifiable off-site",
    d: "Consistent name, address and phone details, real reviews, an active profile, and mentions on sites that describe who you are — these feed the entity understanding that both Google and AI assistants rely on.",
    how: "Claim your profiles, keep NAP identical everywhere, and get named in roundups and interviews, even without a link.",
  },
];

const MYTHS = [
  { t: "Domain Authority is a Google ranking factor", d: "It isn't. It's a third-party estimate built by SEO tool vendors. Google has said repeatedly it has no site-wide authority score of this kind. Use DA to compare yourself to competitors, not to predict rankings." },
  { t: "Buying links is a shortcut", d: "It moves link-based metrics like DR fastest and damages traffic-based ones like Authority Score first. It is also the single most common cause of the manual actions I get called in to clean up." },
  { t: "More pages means more authority", d: "Publishing volume without depth dilutes topical relevance. I have seen sites recover by deleting content, not adding it." },
  { t: "You can go from 10 to 50 in a quarter", d: "The scale is logarithmic. Climbing 10→20 is realistic in a few months. 40→50 can take years." },
];

const TIMELINE = [
  { t: "Month 1", d: "Baseline the score in all three tools, audit referring domains, fix crawl and indexation issues." },
  { t: "Months 2–3", d: "Ship the first citable asset and start outreach. Repair internal linking across the existing site." },
  { t: "Months 4–6", d: "First measurable movement — usually a few points, plus rankings improving before the score does." },
  { t: "Months 6–12", d: "Compounding. Links earned in month 3 are still being passed along, and topical clusters mature." },
];

const FAQS = [
  {
    q: "What is domain authority?",
    a: "Domain authority is a 0–100 score created by SEO tools — Moz calls it Domain Authority, Semrush calls it Authority Score, Ahrefs calls it Domain Rating — that estimates how likely a website is to rank in search results, based mainly on the quality and quantity of sites linking to it. It is a third-party estimate, not a metric Google publishes or uses.",
  },
  {
    q: "How do I improve my domain authority?",
    a: "Earn links from relevant sites that already rank in your niche, remove or disavow toxic links, build complete topical clusters rather than scattered pages, fix internal linking so authority reaches your money pages, keep the technical foundation clean, and make your brand verifiable off-site through consistent listings, reviews and mentions.",
  },
  {
    q: "How long does it take to increase domain authority?",
    a: "Expect the first measurable movement in four to six months and meaningful change in six to twelve. Scores are logarithmic, so early climbs (10 to 20) come far faster than later ones (40 to 50), which can take years of consistent link earning.",
  },
  {
    q: "Is domain authority a Google ranking factor?",
    a: "No. Google has stated it does not use a site-wide authority score like Moz DA. These scores are useful for benchmarking against competitors and tracking your own trend, but rankings are decided page by page on relevance, content quality, links and user signals.",
  },
  {
    q: "What is a good domain authority score?",
    a: "For a new site, anything above 20 signals real traction. 20–40 is a growing site, 40–60 is solid, 60–80 is strong, and 80+ is reserved for major publishers. What matters more is how you compare to the specific sites ranking for your target keywords, not the absolute number.",
  },
  {
    q: "What is the difference between domain authority and page authority?",
    a: "Domain authority estimates the strength of the whole website; page authority estimates the strength of a single URL. A high-authority site can still have weak individual pages, which is why internal linking and per-page content quality matter as much as site-wide metrics.",
  },
  {
    q: "ডোমেইন অথরিটি কিভাবে বাড়ানো যায়? (How to increase domain authority in Bangla?)",
    a: "ডোমেইন অথরিটি বাড়াতে হলে আপনার বিষয়ের সাথে প্রাসঙ্গিক ভালো মানের ওয়েবসাইট থেকে ব্যাকলিংক অর্জন করতে হবে, স্প্যাম বা কেনা লিংক সরিয়ে ফেলতে হবে, একটি বিষয়ের উপর পূর্ণাঙ্গ কনটেন্ট ক্লাস্টার তৈরি করতে হবে, ইন্টারনাল লিংকিং ঠিক করতে হবে এবং সাইটের টেকনিক্যাল সমস্যা (স্পিড, ইনডেক্সিং) সমাধান করতে হবে। সাধারণত ৪–৬ মাসে প্রথম পরিবর্তন দেখা যায়।",
  },
];

export const Route = createFileRoute("/how-to-improve-domain-authority")({
  head: () =>
    buildSeo({
      title: "How To Improve Domain Authority: A Practical 2026 Guide",
      description:
        "What domain authority actually measures, how to check it, and the six things that genuinely move it — plus realistic timelines, common myths, and a Bangla summary.",
      path: "/how-to-improve-domain-authority",
      ogEyebrow: "Guide",
      keywords:
        "how to improve domain authority, increase domain authority, what is domain authority, domain authority checker, website authority, page authority, authority score, domain rating",
      alternates: { bn: "/bn/blog/domain-authority-kivabe-barano" },
    }),
  component: DomainAuthorityPage,
});

function DomainAuthorityPage() {
  return (
    <>
      <Breadcrumbs items={CRUMBS} />

      <Section headingLevel={1}
        eyebrow="Guide"
        title={<>How to improve <span className="text-gradient">domain authority</span></>}
        subtitle="Domain authority is a 0–100 estimate from SEO tools of how likely your site is to rank. You raise it by earning relevant links, removing toxic ones, deepening topical coverage and keeping the technical foundation clean — not by buying links or publishing more pages."
      >
        <div className="max-w-3xl">
          <KeyTakeaways
            items={[
              "Domain authority is a third-party estimate (Moz DA, Semrush AS, Ahrefs DR) — Google does not use it as a ranking factor.",
              "Relevant links from sites that already rank for your topic move it more than any other single action.",
              "Toxic and purchased links are the most common reason a score stalls or falls.",
              "Topical depth and internal linking decide whether the authority you earn actually reaches the pages you want ranked.",
              "Expect first movement in 4–6 months; the scale is logarithmic, so 10→20 is far quicker than 40→50.",
            ]}
          />
          <ReviewedBy
            reviewedOn="2026-08-18"
            credentials="SEO, AEO and GEO consultant based in Rangpur, Bangladesh"
          />
        </div>
      </Section>

      <Section eyebrow="Definition" title="What domain authority actually measures">
        <div className="max-w-3xl space-y-4 text-muted-foreground leading-relaxed -mt-4">
          <p>
            Domain authority is a predictive score built by SEO tool vendors, not by Google. Each
            vendor models the same rough idea — how strong is this site's backlink profile compared
            with everyone else's — and normalises it onto a 0–100 curve.
          </p>
          <p>
            Because the curve is logarithmic, the number is only meaningful next to another number.
            A score of 28 means nothing on its own; a score of 28 against competitors sitting at 24
            and 31 means you are in the same fight and content quality will decide it.
          </p>
        </div>
      </Section>

      <Section eyebrow="Tools" title="How to check your domain authority">
        <div className="grid md:grid-cols-3 gap-6 -mt-4">
          {CHECKERS.map((c, i) => (
            <Reveal key={c.t} delay={(i % 3) * 0.06}>
              <div className="rounded-3xl border border-border bg-card p-7 h-full">
                <h3 className="font-display text-lg">{c.t}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{c.d}</p>
                <p className="mt-3 text-sm text-muted-foreground/80 italic">{c.n}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      <Section eyebrow="The work" title="Six things that actually raise website authority">
        <GuideSteps steps={STEPS} />
      </Section>

      <Section eyebrow="Timeline" title="How long it takes">
        <GuideCardGrid items={TIMELINE} />
      </Section>

      <Section eyebrow="Myths" title="Four things people get wrong about domain authority">
        <GuideCardGrid items={MYTHS} />
      </Section>

      <Section eyebrow="বাংলা" title="ডোমেইন অথরিটি কিভাবে বাড়াবেন?">
        <div lang="bn" className="max-w-3xl space-y-5 text-muted-foreground leading-relaxed -mt-4">
          <p>
            <strong className="text-foreground">ডোমেইন অথরিটি</strong> হলো ০–১০০ স্কেলে একটি স্কোর,
            যা Moz, Semrush বা Ahrefs-এর মতো এসইও টুল হিসাব করে। এটি অনুমান করে আপনার ওয়েবসাইট
            গুগলে র‍্যাংক করার কতটা সম্ভাবনা রাখে। মনে রাখবেন — এটি গুগলের নিজস্ব কোনো র‍্যাংকিং
            ফ্যাক্টর নয়।
          </p>
          <div>
            <h3 className="font-display text-lg text-foreground">অথরিটি বাড়ানোর ধাপ</h3>
            <ul className="mt-3 space-y-2 list-disc pl-5">
              <li>আপনার বিষয়ের সাথে প্রাসঙ্গিক ও বিশ্বস্ত সাইট থেকে <strong className="text-foreground">ব্যাকলিংক</strong> অর্জন করুন।</li>
              <li>স্প্যাম ও কেনা লিংক সরিয়ে ফেলুন বা ডিসঅ্যাভাউ করুন।</li>
              <li>একটি বিষয়ের উপর <strong className="text-foreground">পূর্ণাঙ্গ কনটেন্ট ক্লাস্টার</strong> তৈরি করুন।</li>
              <li>ইন্টারনাল লিংকিং ঠিক করুন, যাতে গুরুত্বপূর্ণ পেজে অথরিটি পৌঁছায়।</li>
              <li>সাইট স্পিড, ইনডেক্সিং ও টেকনিক্যাল সমস্যাগুলো সমাধান করুন।</li>
            </ul>
          </div>
          <div>
            <h3 className="font-display text-lg text-foreground">কত দিন লাগে?</h3>
            <p className="mt-2">
              সাধারণত ৪–৬ মাসে প্রথম পরিবর্তন দেখা যায়। স্কোর যত বাড়ে, পরের ধাপে ওঠা তত কঠিন হয় —
              ১০ থেকে ২০-এ ওঠা যত সহজ, ৪০ থেকে ৫০-এ ওঠা তত কঠিন।
            </p>
          </div>
          <p>
            বাংলায় আরও এসইও গাইড পড়তে{" "}
            <Link to="/bn" className="text-primary underline underline-offset-4">
              বাংলা সেকশন
            </Link>{" "}
            দেখুন।
          </p>
        </div>
      </Section>

      <GuideFooter
        nextTitle="Where to go from here"
        links={[
          { label: "How to check domain authority", to: "/how-to-check-domain-authority" },
          { label: "Moz DA vs Ahrefs DR", to: "/moz-da-vs-ahrefs-dr" },
          { label: "Free SEO audit", to: "/free-seo-audit" },
          { label: "SEO services", to: "/services" },
          { label: "SEO glossary", to: "/glossary" },
          { label: "What is digital marketing?", to: "/what-is-digital-marketing" },
        ]}
        aiContext="website authority"
        aiQuestions={["what-is-entity-seo", "wikipedia-for-aeo", "core-web-vitals-2026", "seo-bangladesh-market"]}
        faqs={FAQS}
        faqIdPrefix="faq-domain-authority"
        ctaTitle={<>Want a look at what's holding your <span className="text-gradient">authority</span> back?</>}
      />

      <JsonLd
        data={faqSchema(FAQS)}
      />
      <JsonLd
        data={{
          "@context": "https://schema.org",
          "@type": "HowTo",
          name: "How to improve domain authority",
          description:
            "A six-step method for raising a website's domain authority score: earn relevant links, remove toxic ones, build topical depth, fix internal linking, resolve technical issues and strengthen off-site brand signals.",
          totalTime: "P6M",
          step: STEPS.map((s, i) => ({
            "@type": "HowToStep",
            position: i + 1,
            name: s.t,
            text: `${s.d} ${s.how}`,
            url: `${SITE.url}/how-to-improve-domain-authority#step-${i + 1}`,
          })),
        }}
      />
      <JsonLd
        data={{
          "@context": "https://schema.org",
          "@type": "DefinedTerm",
          name: "Domain authority",
          description:
            "Domain authority is a 0–100 score produced by SEO tools that estimates how likely a website is to rank in search results, based mainly on the quality and quantity of its backlinks. It is a third-party estimate, not a Google ranking factor.",
          inDefinedTermSet: `${SITE.url}/glossary`,
          url: `${SITE.url}/how-to-improve-domain-authority`,
        }}
      />
      <JsonLd data={breadcrumbSchema(CRUMBS)} />
    </>
  );
}
